// FLAGS: health-data
// Per-theme medical check-in scores for one client, with drill-down into the theme view.
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ResponseVitalityCheckinVitalityCheckinModuleScoreResponse } from "@src/api/v1/data-contracts";
import TableHeader, { Column } from "@src/components/table/TableHeader";
import { useMedicalCheckinOverviewData } from "@src/hooks/useMedicalCheckinOverviewData";
import { useClientMedicalCheckinOverviewQuery } from "@src/queries/clients";
import { formatCheckinDate } from "@src/utils/formatter";
import ClientCheckinsOverviewTable from "./ClientCheckinsOverviewTable";
import { TypographyWithIcon } from "./utils/TypographyWithIcon";

interface ClientMedicalCheckinsOverviewProps {
  clientId: string;
}

const columns: Column[] = [
  { name: "", minWidth: 70 },
  { name: "Checkins.Theme", minWidth: 250 },
  { name: "Checkins.Score", minWidth: 130 },
  { name: "Checkins.Date", minWidth: 200 },
  { name: "Checkins.Change", minWidth: 100 },
];

const ClientMedicalCheckinsOverview = ({
  clientId,
}: ClientMedicalCheckinsOverviewProps) => {
  const { data: medicalThemeData } =
    useClientMedicalCheckinOverviewQuery(clientId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const completeData = useMedicalCheckinOverviewData(medicalThemeData);

  const themeInformation = (
    theme: ResponseVitalityCheckinVitalityCheckinModuleScoreResponse,
  ) => navigate(t(`route.ThemeKey.${theme.themeKey}`));

  if (completeData.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg bg-white shadow-md">
      <div className="p-6">
        <TypographyWithIcon variant="h5" iconLigature="fact_check" outlined>
          {t("Common.Medical.Checkin")}
        </TypographyWithIcon>

        <div className="overflow-x-auto">
          <table
            className="min-w-[650px] w-full mt-8 border-collapse"
            aria-label="simple table"
          >
            <TableHeader columns={columns} />
            <tbody>
              {completeData.map(
                (theme, index) =>
                  theme.questionsWithAnswers &&
                  theme.questionsWithAnswers.length > 0 && (
                    <ClientCheckinsOverviewTable
                      key={theme.themeKey}
                      question={theme}
                      message={t("Checkins.Others")}
                      getCorrectDate={formatCheckinDate}
                      index={index}
                      onClick={() => themeInformation(theme)}
                      isMedical
                    />
                  ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientMedicalCheckinsOverview;
